import React, { useState, useCallback, useRef, useEffect, useMemo } from "react"
import { Modal, Button, Avatar, Typography, Space } from "antd"
import { EnvironmentOutlined, CloseOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import CopyToClipboard from "react-copy-to-clipboard"
import PropTypes from "prop-types"
import styles from "./ShareModal.module.css"
import { Icons } from "assets/icons"

const { Title, Text } = Typography
const DEFAULT_IMAGE = "https://fakeimg.pl/200x200"
const TIMEOUT = 5000

export const ShareListingModal = ({
  visible,
  onClose,
  listingTitle = "",
  listingAddress = "",
  listingImage = "",
  listingLink = ""
}) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const [imageError, setImageError] = useState(false)
  const timeoutRef = useRef(null)

  const clearCopyTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => clearCopyTimeout()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleImageError = () => setImageError(true)

  const closeHandler = () => {
    clearCopyTimeout()
    onClose()
  }

  const onCopyHandler = () => {
    clearCopyTimeout()
    setCopied(true)
    timeoutRef.current = setTimeout(() => setCopied(false), TIMEOUT)
  }

  // Reset copied state when modal opens
  useEffect(() => {
    if (visible) {
      setCopied(false)
      clearCopyTimeout()
    }
  }, [visible, clearCopyTimeout])


  const gmailHref = useMemo(() => {
    const subject = encodeURIComponent(listingTitle)
    const body = encodeURIComponent(listingLink)
    return `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${subject}&body=${body}`
  }, [listingTitle, listingLink])

  const facebookHref = useMemo(() => {
    const url = encodeURIComponent(listingLink)
    const quote = encodeURIComponent(listingTitle)
    return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`
  }, [listingLink, listingTitle])

  const instagramHref = useMemo(() => {
    return `https://www.instagram.com/`
  }, [])

  const twitterHref = useMemo(() => {
    const url = encodeURIComponent(listingLink)
    const text = encodeURIComponent(listingTitle)
    return `https://twitter.com/intent/tweet?url=${url}&text=${text}`
  }, [listingLink, listingTitle])

  const getCopyButtonContent = () => (
    <>
      {copied ? <Icons.Check className={styles.checkIcon} /> : <Icons.CheckIn className={styles.linkIcon} />}
      {copied ? t("experiences.share.snackbar") : t("global.social.copy_link")}
    </>
  )

  return (
    <Modal
      title={listingTitle === "Share Listing" ? t("experiences.share.share_listing") : t("experiences.share.share_experience")}
      open={visible}
      onCancel={closeHandler}
      footer={null}
      centered
      width={431}
      closeIcon={<CloseOutlined />}
      destroyOnClose
      className="shareListingModal"
    >
      <div className={styles.popupInfoHeader}>
        <Avatar
          shape="square"
          size={80}
          src={imageError ? DEFAULT_IMAGE : listingImage}
          alt={listingTitle}
          onError={handleImageError}
        />
        <div className={styles.listInfo}>
          <Title level={5} className={styles.title}>
            {listingTitle}
          </Title>
          <Text type="secondary">
            <EnvironmentOutlined className={styles.locationIcon} />
            {listingAddress}
          </Text>
        </div>
      </div>

      <Space direction="vertical" className={styles.shareButtonContainer} size="middle">
        <Button
          icon={<Icons.Facebook />}
          block
          className={styles.shareBtn}
          href={facebookHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("global.social.facebook")}
        </Button>

        <Button
          icon={<Icons.Instagram />}
          block
          className={styles.shareBtn}
          href={instagramHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("global.social.instagram")}
        </Button>

        <Button
          icon={<Icons.TwitterX />}
          block
          className={styles.shareBtn}
          href={twitterHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("global.social.twitterX")}
        </Button>

        <Button
          icon={<Icons.Mail className={styles.mailIcon} />}
          block
          className={styles.shareBtn}
          href={gmailHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("global.social.email")}
        </Button>

        <CopyToClipboard text={listingLink} onCopy={onCopyHandler}>
          <Button block className={styles.shareBtn} disabled={copied} aria-live="polite">
            {getCopyButtonContent()}
          </Button>
        </CopyToClipboard>
      </Space>
    </Modal>
  )
}

ShareListingModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  listingTitle: PropTypes.string,
  listingAddress: PropTypes.string,
  listingImage: PropTypes.string,
  listingLink: PropTypes.string
}
